import {skipToken} from '@reduxjs/toolkit/query';
import {Action, DataStatus} from 'platform/components';
import {Grid, GridItem, Show, Text, VStack} from 'platform/foundation';

import {useEffect, useState} from 'react';

import {filter, head, isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useCreateAutotracerPlReportMutation,
  useGetAutotracerPlPreviewQuery,
  useGetVehicleAutotracerPlReportsQuery,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {handleApiError, usePermissions, Widget} from '@dms/shared';
import {CebiaWidgetAlert, CebiaWidgetLogo} from '@dms/teas';

import {
  buildArray,
  composePath,
  openInNewTab,
  suffixTestId,
  TestIdProps,
  useNavigate,
} from 'shared/web';

import {ReportStatusLine} from './components/ReportStatusLine';

const POLLING_INTERVAL = 10000;

export interface VehicleHistoryReportWidgetVehicle {
  id: string;
  vin?: string | null;
}

export interface VehicleHistoryReportWidgetProps extends TestIdProps {
  vehicleId: string;
  vehicle?: VehicleHistoryReportWidgetVehicle | null;
}

export function VehicleHistoryReportWidget(props: VehicleHistoryReportWidgetProps) {
  const navigate = useNavigate();
  const [pendingOpenReportId, setPendingOpenReportId] = useState<string | null>(null);
  const [canCreateAutotracerPl] = usePermissions({
    permissionKeys: ['createAutotracerPl'],
  });

  const vehicleId = props.vehicleId;
  const vehicle = props.vehicle;

  const hasVin = isNotNilOrEmpty(vehicle?.vin);

  const [previewPollingInterval, setPreviewPollingInterval] = useState<number | undefined>();
  const [reportsPollingInterval, setReportsPollingInterval] = useState<number | undefined>();

  const {
    data: preview,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useGetAutotracerPlPreviewQuery(isNotNil(vehicleId) ? {vehicleId} : skipToken, {
    skip: !hasVin,
    pollingInterval: previewPollingInterval,
  });

  const {data: reports, isError: isReportsError} = useGetVehicleAutotracerPlReportsQuery(
    isNotNil(vehicleId) ? {vehicleId} : skipToken,
    {
      pollingInterval: reportsPollingInterval,
    }
  );

  const [createReport, {isLoading: isCreating}] = useCreateAutotracerPlReportMutation();

  const isPreviewContentLoading = hasVin && (isPreviewLoading || preview?.status === 'pending');

  const latestReport = head(reports ?? []);
  const isLatestReportPending =
    latestReport?.status === 'accepted' || latestReport?.status === 'pending';
  const latestCompletedReport = head(
    filter((report) => report.status === 'completed', reports ?? [])
  );
  const canViewLatestReport =
    isNotNil(latestCompletedReport?.url) || isNotNil(latestCompletedReport?.pdfUrl);

  useEffect(
    function syncPreviewPollingInterval() {
      setPreviewPollingInterval(preview?.status === 'pending' ? POLLING_INTERVAL : undefined); // eslint-disable-line react/set-state-in-effect -- Pre-existing violation
    },
    [preview?.status]
  );

  useEffect(
    function syncReportsPollingInterval() {
      setReportsPollingInterval(isLatestReportPending ? POLLING_INTERVAL : undefined); // eslint-disable-line react/set-state-in-effect -- Pre-existing violation
    },
    [isLatestReportPending]
  );

  useEffect(
    function openPendingReportWhenReady() {
      if (
        isNil(pendingOpenReportId) ||
        isNil(latestReport) ||
        latestReport.id !== pendingOpenReportId
      ) {
        return;
      }
      const url = latestReport.url ?? latestReport.pdfUrl;
      if (isNotNil(url)) {
        openInNewTab(url);

        setPendingOpenReportId(null); // eslint-disable-line react/set-state-in-effect -- Pre-existing violation
      }
    },
    [latestReport, pendingOpenReportId]
  );

  const handleGetReport = () => {
    createReport({vehicleId})
      .unwrap()
      .then((response) => {
        setPendingOpenReportId(response.id);
      })
      .catch(handleApiError);
  };

  const handleViewReport = () => {
    const url = latestCompletedReport?.url ?? latestCompletedReport?.pdfUrl;
    if (isNotNil(url)) {
      openInNewTab(url);
    }
  };

  return (
    <VStack spacing={2}>
      <Widget.Card data-testid={suffixTestId('vehicleHistoryReportWidget', props)}>
        <Widget.Header
          logo={<CebiaWidgetLogo alt={i18n.t('page.integrations.labels.autotracerPlTitle')} />}
          title={i18n.t('page.integrations.labels.autotracerPlTitle')}
          subtitle={
            <Text size="xSmall" color="secondary">
              {i18n.t('page.integrations.labels.autotracerPlDescription')}
            </Text>
          }
          actions={buildArray<Action>()
            .when(isNotNil(latestCompletedReport), {
              type: 'button',
              variant: 'secondary',
              rightIcon: 'action/launch',
              isDisabled: !canViewLatestReport,
              onClick: handleViewReport,
              title: i18n.t('page.integrations.labels.cebiaAutotracerViewReport'),
              'data-testid': suffixTestId('vehicleHistoryReportWidget-viewReport', props),
            })
            .add({
              type: 'button',
              variant: 'secondary',
              isDisabled: !hasVin || !canCreateAutotracerPl,
              isLoading: isCreating || isLatestReportPending,
              onClick: handleGetReport,
              title: i18n.t('page.integrations.labels.cebiaAutotraceGetReport'),
              'data-testid': suffixTestId('vehicleHistoryReportWidget-getReport', props),
            })}
          data-testid={suffixTestId('vehicleHistoryReportWidget', props)}
        />
        <Show when={hasVin}>
          <DataStatus isError={isPreviewError || isReportsError}>
            <Widget.Content data-testid={suffixTestId('vehicleHistoryReportWidget', props)}>
              <Grid columns={[1, 2]} spacing={2}>
                <GridItem>
                  <ReportStatusLine
                    icon="specification/speedometer"
                    label={i18n.t('page.integrations.labels.autotracerPlMileage')}
                    value={
                      preview?.isMileageAvailable
                        ? i18n.t('page.integrations.labels.autotracerPlAvailable')
                        : i18n.t('page.integrations.labels.autotracerPlNotAvailable')
                    }
                    tone={preview?.isMileageAvailable ? 'success' : 'informational'}
                    isLoading={isPreviewContentLoading}
                  />
                </GridItem>
                <GridItem>
                  <ReportStatusLine
                    icon="automotive/car_star_shining"
                    label={i18n.t('page.integrations.labels.autotracerPlDamage')}
                    value={
                      preview?.isDamagesAvailable
                        ? i18n.t('page.integrations.labels.autotracerPlRecordFound')
                        : i18n.t('page.integrations.labels.autotracerPlNoRecord')
                    }
                    tone={preview?.isDamagesAvailable ? 'warning' : 'success'}
                    isLoading={isPreviewContentLoading}
                  />
                </GridItem>
              </Grid>
            </Widget.Content>
          </DataStatus>
        </Show>
      </Widget.Card>
      <Show when={isNotNil(vehicle) && !hasVin}>
        <CebiaWidgetAlert
          colorScheme="warning"
          message={i18n.t('entity.vehicle.notifications.vinIsRequired')}
          actionText={i18n.t('entity.vehicle.labels.editVehicle')}
          actionCallback={() =>
            navigate(composePath(vehiclesRoutes.editV2, {params: {id: vehicleId}}))
          }
        />
      </Show>
    </VStack>
  );
}
